import { type Action, isAnyOf, type ListenerEffectAPI } from '@reduxjs/toolkit';
import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import { getWorkspaceMatchFulfilled } from '@/api/workspace';
import { espApi } from '@/store/esp/api';
import { espStreamSubscribed, espStreamUnsubscribed } from '@/store/esp/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { addAppListener, type AppListenerEffect } from '@/store/storeMethods.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { addTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import { getTodayDateOnly } from '@/utils/date.ts';

async function registerListener(tileId: string, api: ListenerEffectAPI<AppState, AppDispatch>) {
  // listen to any events which can require to subscribe to ESP or unsubscribe from it
  const allEffectsAfterTileChanged = [tieringEffect(tileId), espSubscriptionEffect(tileId)];

  const unsubscribe = api.dispatch(
    addAppListener({
      predicate: (action, currentState, previousState) => {
        return (
          !optionSlice.actions.changeClient.match(action) &&
          !optionSlice.actions.optionClientIdUpdated.match(action) &&
          isOptionTileChanged(tileId, previousState, currentState)
        );
      },

      effect: (action, api) => {
        for (const effect of allEffectsAfterTileChanged) {
          effect(action, api);
        }
      },
    }),
  );

  const isOption = api.getOriginalState().tiles.entities[tileId]?.productType === 'Option';

  if (await api.condition(unsubscribeCondition(tileId, isOption))) {
    unsubscribe();
  }
}

export const registerEspOptionListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    actionCreator: addTile,

    effect: async (action, api) => {
      const {
        tile: { tileId },
      } = action.payload;

      await registerListener(tileId, api);
    },
  });

  startAppListening({
    actionCreator: updateTileProductType,

    effect: async (action, api) => {
      const { tileId } = action.payload;
      await registerListener(tileId, api);
    },
  });

  startAppListening({
    matcher: getWorkspaceMatchFulfilled,

    effect: async (_action, api) => {
      const currentState = api.getState();
      for (const product of optionSlice.selectors.getAllOptionProducts(currentState)) {
        const { tileId } = product;
        await registerListener(tileId, api);
      }
    },
  });

  startAppListening({
    matcher: isAnyOf(optionSlice.actions.changeClient, optionSlice.actions.optionClientIdUpdated),
    effect: async (action, api) => {
      const tileId =
        optionSlice.actions.changeClient.match(action) || optionSlice.actions.optionClientIdUpdated.match(action)
          ? action.payload.tileId
          : undefined;
      if (isNotDefined(tileId)) {
        return;
      }

      if (
        optionSlice.actions.changeClient.match(action) &&
        api.getOriginalState().option[tileId]?.clientId === action.payload.clientId
      ) {
        return;
      }

      espUnsubscriptionEffect(tileId)(action, api);
      await tieringEffect(tileId)(action, api);
    },
  });

  startAppListening({
    predicate: isAnyOf(removeTile, updateTileProductType),
    effect: async (action, api) => {
      const tileId = removeTile.match(action)
        ? action.payload
        : updateTileProductType.match(action)
          ? action.payload.tileId
          : undefined;

      if (isDefined(tileId) && api.getOriginalState().tiles.entities[tileId]?.productType === 'Option') {
        espUnsubscriptionEffect(tileId)(action, api);
      }
    },
  });
};

function unsubscribeCondition(tileId: string, isOption: boolean) {
  return (action: Action) => {
    return (
      (removeTile.match(action) && action.payload === tileId) ||
      (isOption && updateTileProductType.match(action) && action.payload.tileId === tileId)
    );
  };
}

const tieringEffect =
  (tileId: string): AppListenerEffect =>
  async (_action, api) => {
    const state = api.getState();
    const currentTile = state.option[tileId];
    if (isNotDefined(currentTile)) {
      return;
    }

    // has already a tiering ?
    if (isDefined(currentTile.properties.tiering)) {
      return;
    }

    // all required parameters are not ready ?
    if (isEmpty(currentTile.properties.currencyPair)) {
      return;
    }

    const result = await api.dispatch(
      espApi.endpoints.getEspTiering.initiate({
        clientId: selectTileClientId(state, tileId),
        currencyPair: currentTile.properties.currencyPair,
        spotDate: getTodayDateOnly(),
      }),
    );

    if ('data' in result && isDefined(result.data) && !('error' in result.data)) {
      api.dispatch(optionSlice.actions.optionTieringUpdated({ tileId, tiering: result.data.tiering }));
    }
  };

const espSubscriptionEffect =
  (tileId: string): AppListenerEffect =>
  (_action, api) => {
    const currentState = api.getState();

    const currentTile = currentState.option[tileId];

    if (isNotDefined(currentTile)) {
      return;
    }

    // all required parameters are not ready ?
    if (isEmpty(currentTile.properties.currencyPair) || isEmpty(currentTile.properties.tiering)) {
      return;
    }

    const espId = getEspStreamId(currentTile.properties.currencyPair, currentTile.properties.tiering);
    const espStream = currentState.esp.streams[espId];

    // has already subscribed ?
    if (isDefined(espStream) && espStream.subscribedTileIds.includes(tileId)) {
      return;
    }

    api.dispatch(
      espStreamSubscribed({
        tileId,
        currencyPair: currentTile.properties.currencyPair,
        tiering: currentTile.properties.tiering,
        bdrId: selectTileClientId(currentState, tileId),
      }),
    );
  };

const espUnsubscriptionEffect =
  (tileId: string): AppListenerEffect =>
  (_action, api) => {
    const currentState = api.getState();

    const previousTile = api.getOriginalState().option[tileId];
    const currentTile = currentState.option[tileId];
    if (isNotDefined(previousTile)) {
      return;
    }

    // all required parameters for ESP not present ?
    if (isEmpty(previousTile.properties.currencyPair) || isEmpty(previousTile.properties.tiering)) {
      return;
    }

    const espId = getEspStreamId(previousTile.properties.currencyPair, previousTile.properties.tiering);
    const espStream = currentState.esp.streams[espId];

    // not previously subscribed ?
    if (isNotDefined(espStream) || !espStream.subscribedTileIds.includes(tileId)) {
      return;
    }

    // current tile is still compatible with ESP ?
    if (
      isDefined(currentTile) &&
      currentTile.properties.currencyPair === previousTile.properties.currencyPair &&
      currentTile.properties.tiering === previousTile.properties.tiering
    ) {
      return;
    }

    api.dispatch(
      espStreamUnsubscribed({
        tileId,
        currencyPair: previousTile.properties.currencyPair,
        tiering: previousTile.properties.tiering,
      }),
    );
  };

const isOptionTileChanged = (tileId: string, previousState: AppState, currentState: AppState) => {
  const currentTile = currentState.option[tileId];
  const previousTile = previousState.option[tileId];
  return currentTile !== previousTile;
};
